import type React from 'react';
import { memo, useMemo } from 'react';
import type { AllowedTags, Components } from 'streamdown';
import {
  type CitationChunkInfo,
  type CitationChunkInfoRequest,
  useCitationChunkInfo,
} from '@/hooks/useCitationTooltips';
import { cn } from '@/lib/utils';
import { documentDisplayName } from '@/utils/file-utils';
import {
  buildCitationNumberingMap,
  CITATION_MARKER_PATTERN,
  CITATION_UUID_PATTERN,
  type Document,
  docketCitationHref,
  isValidCitation,
  lookupWebCitationDisplay,
  parseCitationMarker as parseCanonicalCitationMarker,
  parseSources,
  renderDeletedCitationHtml,
  type Source,
  type WebCitationDisplay,
} from '@/utils/citation-utils';
import {
  getFenceMarker,
  isPipeRow,
  isSeparatorRow,
  nextFenceState,
  splitPipeRow,
} from '@/utils/markdown-blocks';
import { LiveMarkdown } from './live-markdown';

type TableAlignment = 'left' | 'center' | 'right';
const CITATION_CLUSTER_SEPARATOR_SOURCE = String.raw`[^\S\r\n]*(?:[,;][^\S\r\n]*)?`;
const COMPLETE_CITATION_PATTERN = new RegExp(CITATION_MARKER_PATTERN, 'gi');
const DEADLINE_MARKER_PATTERN = new RegExp(
  `\\[deadline:([^\\]]+)\\]`,
  'gi'
);
const CITATION_CLUSTER_PATTERN = new RegExp(
  `${CITATION_MARKER_PATTERN}(?:${CITATION_CLUSTER_SEPARATOR_SOURCE}${CITATION_MARKER_PATTERN})+`,
  'gi'
);
const CITATION_CLUSTER_WITH_DANGLING_DASH_PATTERN = new RegExp(
  `(${CITATION_MARKER_PATTERN}(?:${CITATION_CLUSTER_SEPARATOR_SOURCE}${CITATION_MARKER_PATTERN})*)[\\t ]*[—–-](?=[\\t ]*(?:\\||\\r?\\n|$))`,
  'gi'
);
const MALFORMED_CITATION_PATTERN = /\[(?:cite|web):[^\]]+\]/gi;
const INCOMPLETE_CITATION_PATTERN = /\[(?:c(?:i(?:t(?:e(?::[^\]]*)?)?)?)?|w(?:e(?:b(?::[^\]]*)?)?)?)?$/i;
const CITATION_PLACEHOLDER_FRAGMENT_PATTERN = /@@crimson-(?:cite|web):[a-z0-9:._@%-]*(?:@@)?/gi;
const CITATIONS_SECTION_PATTERN = /^##\s*Citations\b/mi;
const LEGACY_CITATION_PATTERN = /\[cite:\d+\]|@@crimson-cite:\d+@@/;
const EMPTY_SOURCES = new Map<string, Source>();
type StreamdownElementProps<Tag extends keyof React.JSX.IntrinsicElements> =
  React.JSX.IntrinsicElements[Tag] & { node?: unknown };
const CASE_PARTNER_MARKDOWN_COMPONENTS = {
  h1: ({ node: _node, ...props }: StreamdownElementProps<'h1'>) => <h1 {...props} />,
  h2: ({ node: _node, ...props }: StreamdownElementProps<'h2'>) => <h2 {...props} />,
  h3: ({ node: _node, ...props }: StreamdownElementProps<'h3'>) => <h3 {...props} />,
  h4: ({ node: _node, ...props }: StreamdownElementProps<'h4'>) => <h4 {...props} />,
  h5: ({ node: _node, ...props }: StreamdownElementProps<'h5'>) => <h5 {...props} />,
  h6: ({ node: _node, ...props }: StreamdownElementProps<'h6'>) => <h6 {...props} />,
  ol: ({ node: _node, ...props }: StreamdownElementProps<'ol'>) => <ol {...props} />,
  ul: ({ node: _node, ...props }: StreamdownElementProps<'ul'>) => <ul {...props} />,
  li: ({ node: _node, ...props }: StreamdownElementProps<'li'>) => <li {...props} />,
  // Streamdown's default `a` becomes a <button> when link-safety is on and
  // drops data-* attributes. Citation links must stay real anchors so the
  // browser's "Open in New Tab" works; AppShell still intercepts left-click
  // for in-app navigation. Ordinary markdown links open in a new tab;
  // rehype-harden has already filtered their hrefs.
  a: ({ node: _node, href, className, children, ...props }: StreamdownElementProps<'a'>) => {
    const isCitationLink =
      typeof className === 'string' && className.split(/\s+/).includes('citation-link');
    if (isCitationLink) {
      return (
        <a className={className} href={href} {...props}>
          {children}
        </a>
      );
    }
    return (
      <a
        className={cn('wrap-anywhere font-medium text-primary underline', className)}
        href={href}
        rel="noreferrer"
        target="_blank"
        {...props}
      >
        {children}
      </a>
    );
  },
  hr: () => null,
} satisfies Components;
const CASE_PARTNER_STREAMDOWN_CLASSES = [
  'space-y-0',
  '[&>h1:first-child]:mt-4',
  '[&>h2:first-child]:mt-4',
  '[&>h3:first-child]:mt-4',
  '[&>h4:first-child]:mt-4',
  '[&>h5:first-child]:mt-4',
  '[&>h6:first-child]:mt-4',
  '[&>ol:first-child]:mt-2',
  '[&>p:first-child]:mt-2',
  '[&>pre:first-child]:mt-2',
  '[&>ul:first-child]:mt-2',
  '[&>h1:last-child]:mb-2',
  '[&>h2:last-child]:mb-2',
  '[&>h3:last-child]:mb-2',
  '[&>h4:last-child]:mb-2',
  '[&>h5:last-child]:mb-2',
  '[&>h6:last-child]:mb-2',
  '[&>ol:last-child]:mb-2',
  '[&>p:last-child]:mb-2',
  '[&>pre:last-child]:mb-2',
  '[&>ul:last-child]:mb-2',
].join(' ');
const CITATION_ELEMENT_ATTRIBUTES = [
  'aria-label',
  'ariaLabel',
  'aria-hidden',
  'ariaHidden',
  'class',
  'className',
  'data-cell-range',
  'data-chunk-id',
  'data-citation-key',
  'data-citation-state',
  'data-cite',
  'data-display-number',
  'data-deadline-id',
  'data-doc-guid',
  'data-docket-entry-guid',
  'data-file-name',
  'data-page-number',
  'data-paragraph-number',
  'data-position',
  'data-segment-index',
  'data-site-name',
  'data-web-url',
  'dataCellRange',
  'dataChunkId',
  'dataCitationKey',
  'dataCitationState',
  'dataCite',
  'dataDisplayNumber',
  'dataDeadlineId',
  'dataDocGuid',
  'dataDocketEntryGuid',
  'dataFileName',
  'dataPageNumber',
  'dataParagraphNumber',
  'dataPosition',
  'dataSegmentIndex',
  'dataSiteName',
  'dataWebUrl',
  'role',
  'tabIndex',
  'tabindex',
  'title',
] as const;
const CITATION_ALLOWED_TAGS = {
  a: [...CITATION_ELEMENT_ATTRIBUTES, 'href', 'rel', 'target'],
  span: [...CITATION_ELEMENT_ATTRIBUTES],
  button: [
    'aria-label',
    'ariaLabel',
    'class',
    'className',
    'data-deadline-id',
    'dataDeadlineId',
    'title',
    'type',
  ],
  svg: [
    'aria-hidden',
    'ariaHidden',
    'class',
    'className',
    'fill',
    'height',
    'stroke',
    'stroke-linecap',
    'stroke-linejoin',
    'stroke-width',
    'viewBox',
    'width',
    'xmlns',
  ],
  rect: ['height', 'rx', 'ry', 'width', 'x', 'y'],
  line: ['x1', 'x2', 'y1', 'y2'],
} satisfies AllowedTags;

/** Lucide-style calendar glyph for inline deadline chips (HTML string path). */
const DEADLINE_CALENDAR_ICON_HTML =
  '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class="deadline-link-icon">'
  + '<rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect>'
  + '<line x1="16" x2="16" y1="2" y2="6"></line>'
  + '<line x1="8" x2="8" y1="2" y2="6"></line>'
  + '<line x1="3" x2="21" y1="10" y2="10"></line>'
  + '</svg>';

export interface TextMessagePart {
  id: string;
  type: 'text';
  content: string;
}

export interface TableMessagePart {
  id: string;
  type: 'table';
  columns: Array<{
    label: string;
    align?: TableAlignment;
  }>;
  rows: string[][];
  isPartial?: boolean;
}

export interface CustomMessagePart {
  id: string;
  type: 'custom';
  name: string;
  props: Record<string, unknown>;
}

export type MessagePart = TextMessagePart | TableMessagePart | CustomMessagePart;

export type CustomMessagePartRenderers = Record<
  string,
  (props: { part: CustomMessagePart; isStreaming?: boolean }) => React.ReactNode
>;

interface ParseMarkdownMessagePartsOptions {
  splitTextBlocks?: boolean;
}

interface MessagePartsProps {
  content?: string;
  parts?: MessagePart[];
  isStreaming?: boolean;
  className?: string;
  customRenderers?: CustomMessagePartRenderers;
  documents?: Document[];
  flushBottom?: boolean;
  flushTop?: boolean;
  hideCitations?: boolean;
  slug?: string;
  webCitationMeta?: ReadonlyMap<string, WebCitationDisplay>;
}

interface CitationContext {
  chunkInfoRequests: CitationChunkInfoRequest[];
  citationNumbers: Map<string, string>;
  documentsByGuid?: Map<string, Document>;
  slug?: string;
  sources: Map<string, Source>;
  webCitationMeta?: ReadonlyMap<string, WebCitationDisplay>;
}

interface ResolvedCitation {
  cellRange?: string;
  chunkId?: number;
  displayNumber: string;
  docketEntryGuid?: string;
  documentGuid?: string;
  fileName?: string;
  href?: string;
  isDeleted: boolean;
  legacyNumber?: string;
  pageNumber?: number;
  paragraphNumber?: number;
  requestKey?: string;
  segmentIndex?: number;
  siteName?: string;
}

interface ParsedCitationMarker {
  cellRange?: string;
  chunkId?: string;
  docketEntryGuid?: string;
  guid?: string;
  href?: string;
  key: string;
  legacyNumber?: string;
  segmentIndex?: string;
}

export function parseMarkdownMessageParts(
  content: string,
  options: ParseMarkdownMessagePartsOptions = {}
): MessagePart[] {
  if (!content) return [];

  const lines = content.split('\n');
  const parts: MessagePart[] = [];
  let textStartLine = 0;
  let textBuffer: string[] = [];
  let lineIndex = 0;
  let fenceMarker: string | null = null;

  const flushText = () => {
    appendTextParts(parts, textBuffer, textStartLine, options);
    textBuffer = [];
    textStartLine = lineIndex;
  };

  while (lineIndex < lines.length) {
    const currentFenceMarker = getFenceMarker(lines[lineIndex]);
    if (currentFenceMarker) {
      fenceMarker = nextFenceState(fenceMarker, lines[lineIndex]);
      textBuffer.push(lines[lineIndex]);
      lineIndex += 1;
      continue;
    }

    const table = fenceMarker
      ? null
      : parsePipeTableAt(lines, lineIndex, content.endsWith('\n'));

    if (table) {
      flushText();
      parts.push({
        id: `table-${lineIndex}`,
        type: 'table',
        columns: table.columns,
        rows: table.rows,
        isPartial: table.isPartial,
      });
      lineIndex = table.nextLineIndex;
      textStartLine = lineIndex;
      continue;
    }

    textBuffer.push(lines[lineIndex]);
    lineIndex += 1;
  }

  flushText();
  return parts;
}

function appendTextParts(
  parts: MessagePart[],
  lines: string[],
  startLine: number,
  options: ParseMarkdownMessagePartsOptions
) {
  if (options.splitTextBlocks) {
    appendSplitTextParts(parts, lines, startLine);
    return;
  }

  const content = lines.join('\n').trim();
  if (!content) return;

  parts.push({
    id: `text-${startLine}`,
    type: 'text',
    content,
  });
}

function appendSplitTextParts(parts: MessagePart[], lines: string[], startLine: number) {
  let blockStartLine = startLine;
  let blockLines: string[] = [];
  let fenceMarker: string | null = null;

  const flushBlock = () => {
    if (blockLines.length === 0) return;

    parts.push({
      id: `text-${blockStartLine}`,
      type: 'text',
      content: blockLines.join('\n'),
    });
    blockLines = [];
  };

  lines.forEach((line, index) => {
    fenceMarker = nextFenceState(fenceMarker, line);

    if (!fenceMarker && line.trim() === '') {
      flushBlock();
      blockStartLine = startLine + index + 1;
      return;
    }

    if (blockLines.length === 0) {
      blockStartLine = startLine + index;
    }
    blockLines.push(line);
  });

  flushBlock();
}

function areTablePropsEqual(
  previous: { part: TableMessagePart },
  next: { part: TableMessagePart }
) {
  return areTablePartsEqual(previous.part, next.part);
}

function areTablePartsEqual(previous: TableMessagePart, next: TableMessagePart) {
  if (previous === next) return true;
  if (
    previous.id !== next.id ||
    previous.isPartial !== next.isPartial ||
    previous.columns.length !== next.columns.length ||
    previous.rows.length !== next.rows.length
  ) {
    return false;
  }

  for (let index = 0; index < previous.columns.length; index += 1) {
    if (
      previous.columns[index].label !== next.columns[index].label ||
      previous.columns[index].align !== next.columns[index].align
    ) {
      return false;
    }
  }

  for (let rowIndex = 0; rowIndex < previous.rows.length; rowIndex += 1) {
    const previousRow = previous.rows[rowIndex];
    const nextRow = next.rows[rowIndex];
    if (previousRow.length !== nextRow.length) {
      return false;
    }

    for (let cellIndex = 0; cellIndex < previousRow.length; cellIndex += 1) {
      if (previousRow[cellIndex] !== nextRow[cellIndex]) {
        return false;
      }
    }
  }

  return true;
}

const MessageTable = memo(function MessageTable({ part }: { part: TableMessagePart }) {
  return (
    <div className="not-prose max-w-full overflow-x-auto">
      <table className="m-0 w-full table-auto border-collapse text-xs text-slate-700">
        <thead className="bg-slate-50">
          <tr>
            {part.columns.map((column, columnIndex) => (
              <th
                className={cn(
                  'min-w-40 border border-slate-200 bg-slate-50 px-2 py-1 text-left align-top font-semibold text-slate-600 text-xs whitespace-normal break-words',
                  getAlignmentClass(column.align)
                )}
                key={`${part.id}-heading-${columnIndex}`}
              >
                <InlineCellMarkdown content={column.label} isStreaming={Boolean(part.isPartial)} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {part.rows.map((row, rowIndex) => (
            <tr
              className="even:bg-slate-50 hover:bg-slate-100"
              key={`${part.id}-row-${rowIndex}`}
            >
              {part.columns.map((column, columnIndex) => (
                <td
                  className={cn(
                    'min-w-40 border border-slate-200 px-2 py-1 align-top text-xs whitespace-normal break-words',
                    getAlignmentClass(column.align)
                  )}
                  key={`${part.id}-cell-${rowIndex}-${columnIndex}`}
                >
                  <InlineCellMarkdown content={row[columnIndex] ?? ''} isStreaming={Boolean(part.isPartial)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}, areTablePropsEqual);

const InlineCellMarkdown = memo(function InlineCellMarkdown({
  content,
  isStreaming = false,
}: {
  content: string;
  isStreaming?: boolean;
}) {
  if (!content) return null;

  return (
    <LiveMarkdown
      allowedTags={CITATION_ALLOWED_TAGS}
      className="max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:my-0 [&_strong]:font-semibold [&_em]:italic [&_code]:rounded-sm [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-px [&_code]:font-medium [&_code]:text-slate-700 [&_code]:before:content-none [&_code]:after:content-none"
      components={CASE_PARTNER_MARKDOWN_COMPONENTS}
      content={content}
      isStreaming={isStreaming}
    />
  );
});

const THEMATIC_BREAK_LINE_PATTERN = /^\s{0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/;

function isHorizontalRulePart(content: string) {
  return THEMATIC_BREAK_LINE_PATTERN.test(content);
}

export function stripMarkdownThematicBreaks(content: string): string {
  const lines = content.split('\n');
  const kept: string[] = [];
  let fenceMarker: string | null = null;

  for (const line of lines) {
    const currentFenceMarker = getFenceMarker(line);
    if (currentFenceMarker) {
      fenceMarker = nextFenceState(fenceMarker, line);
      kept.push(line);
      continue;
    }

    if (!fenceMarker && THEMATIC_BREAK_LINE_PATTERN.test(line)) {
      continue;
    }

    kept.push(line);
  }

  return collapseBlankLinesOutsideFences(kept.join('\n'));
}

function collapseBlankLinesOutsideFences(content: string): string {
  const lines = content.split('\n');
  const kept: string[] = [];
  let fenceMarker: string | null = null;
  let pendingBlanks = 0;

  const flushBlanks = () => {
    const allow = fenceMarker ? pendingBlanks : Math.min(pendingBlanks, 1);
    for (let index = 0; index < allow; index += 1) {
      kept.push('');
    }
    pendingBlanks = 0;
  };

  for (const line of lines) {
    if (getFenceMarker(line)) {
      flushBlanks();
      fenceMarker = nextFenceState(fenceMarker, line);
      kept.push(line);
      continue;
    }
    if (!fenceMarker && line === '') {
      pendingBlanks += 1;
      continue;
    }
    if (fenceMarker && line === '') {
      kept.push(line);
      continue;
    }
    flushBlanks();
    kept.push(line);
  }
  flushBlanks();

  return kept.join('\n');
}

export const MessageParts = memo(function MessageParts({
  content = '',
  parts,
  isStreaming = false,
  className,
  customRenderers,
  documents,
  flushBottom = false,
  flushTop = false,
  hideCitations = false,
  slug,
  webCitationMeta,
}: MessagePartsProps) {
  const citationContext = useMemo(
    () => buildCitationContext(content, documents, slug, webCitationMeta),
    [content, documents, slug, webCitationMeta]
  );
  const chunkInfoByRequestKey = useCitationChunkInfo(
    slug,
    citationContext.chunkInfoRequests
  );
  const renderableContent = useMemo(
    () => prepareContentForRendering(content, citationContext, chunkInfoByRequestKey),
    [content, citationContext, chunkInfoByRequestKey]
  );
  const resolvedParts = useMemo(
    () => parts ?? parseMarkdownMessageParts(renderableContent, { splitTextBlocks: isStreaming }),
    [isStreaming, parts, renderableContent]
  );

  if (resolvedParts.length === 0) return null;

  return (
    <div className={cn('flex flex-col gap-2', className, hideCitations && 'citations-hidden')}>
      {resolvedParts.map((part, index) => {
        if (part.type === 'text') {
          if (isHorizontalRulePart(part.content)) {
            return null;
          }

          return (
            <LiveMarkdown
              allowedTags={CITATION_ALLOWED_TAGS}
              className={cn(
                CASE_PARTNER_STREAMDOWN_CLASSES,
                flushTop && index === 0 && '[&>:first-child]:!mt-0',
                flushBottom
                  && index === resolvedParts.length - 1
                  && '[&>:last-child]:!mb-0',
              )}
              components={CASE_PARTNER_MARKDOWN_COMPONENTS}
              content={part.content}
              isStreaming={isStreaming}
              key={part.id}
            />
          );
        }

        if (part.type === 'table') {
          return <MessageTable key={part.id} part={part} />;
        }

        const CustomRenderer = customRenderers?.[part.name];
        if (!CustomRenderer) return null;
        return <CustomRenderer isStreaming={isStreaming} key={part.id} part={part} />;
      })}
    </div>
  );
});

function buildCitationContext(
  content: string,
  documents?: Document[],
  slug?: string,
  webCitationMeta?: ReadonlyMap<string, WebCitationDisplay>,
): CitationContext {
  const visibleContent = removeCitationsSection(content);
  const citationNumbers = new Map<string, string>();
  const documentsByGuid = documents
    ? new Map(documents.map((document) => [document.guid, document]))
    : undefined;
  const chunkInfoRequestsByKey = new Map<string, CitationChunkInfoRequest>();
  const sources =
    CITATIONS_SECTION_PATTERN.test(content) || LEGACY_CITATION_PATTERN.test(visibleContent)
      ? parseSources(content, documents)
      : EMPTY_SOURCES;
  const canonicalNumbers = buildCitationNumberingMap(visibleContent);

  for (const match of visibleContent.matchAll(COMPLETE_CITATION_PATTERN)) {
    const citation = parseCitationMarker(match[0]);
    if (!citation || citationNumbers.has(citation.key)) {
      continue;
    }

    citationNumbers.set(
      citation.key,
      String(canonicalNumbers.get(citation.key) ?? citation.legacyNumber ?? '?')
    );

    const request = getCitationChunkInfoRequest(citation, sources);
    if (request) {
      chunkInfoRequestsByKey.set(getChunkInfoRequestKey(request), request);
    }
  }

  COMPLETE_CITATION_PATTERN.lastIndex = 0;

  return {
    chunkInfoRequests: Array.from(chunkInfoRequestsByKey.values()),
    citationNumbers,
    documentsByGuid,
    slug,
    sources,
    webCitationMeta,
  };
}


function renderDeadlineMarkerHtml(deadlineId: string) {
  const attributes: Array<[string, string]> = [
    ['type', 'button'],
    ['class', 'deadline-link'],
    ['aria-label', 'Open deadline'],
    ['data-deadline-id', deadlineId],
    ['title', 'Open deadline'],
  ];
  const serialized = attributes
    .map(([name, value]) => `${name}="${escapeHtmlAttribute(value)}"`)
    .join(' ');
  return `<button ${serialized}>${DEADLINE_CALENDAR_ICON_HTML}</button>`;
}

function prepareContentForRendering(
  content: string,
  citationContext: CitationContext,
  chunkInfoByRequestKey: Map<string, CitationChunkInfo | null>
) {
  const visibleContent = stripMarkdownThematicBreaks(
    removeDanglingDashAfterCitationClusters(removeCitationsSection(content)),
  );

  return visibleContent
    .replace(DEADLINE_MARKER_PATTERN, (_match, deadlineId: string) => {
      return renderDeadlineMarkerHtml(deadlineId);
    })
    .replace(CITATION_CLUSTER_PATTERN, (cluster) => {
      return renderCitationCluster(cluster, citationContext, chunkInfoByRequestKey);
    })
    .replace(COMPLETE_CITATION_PATTERN, (marker) => {
      return renderCitationMarker(marker, citationContext, chunkInfoByRequestKey);
    })
    .replace(CITATION_PLACEHOLDER_FRAGMENT_PATTERN, '')
    .replace(INCOMPLETE_CITATION_PATTERN, '')
    .replace(MALFORMED_CITATION_PATTERN, '');
}

function removeDanglingDashAfterCitationClusters(content: string) {
  return content.replace(CITATION_CLUSTER_WITH_DANGLING_DASH_PATTERN, '$1');
}

function renderCitationCluster(
  cluster: string,
  citationContext: CitationContext,
  chunkInfoByRequestKey: Map<string, CitationChunkInfo | null>
) {
  const items: Array<{
    prefix: string;
    citation: ResolvedCitation;
  }> = [];
  let lastIndex = 0;

  for (const match of cluster.matchAll(COMPLETE_CITATION_PATTERN)) {
    const parsedCitation = parseCitationMarker(match[0]);
    if (!parsedCitation) continue;

    const {
      cellRange,
      chunkId,
      docketEntryGuid,
      guid,
      href,
      key,
      legacyNumber,
      segmentIndex,
    } = parsedCitation;
    const citation = resolveCitation({
      citationContext,
      cellRange,
      chunkId,
      docketEntryGuid,
      guid,
      href,
      key,
      legacyNumber,
      segmentIndex,
    });
    if (!citation) continue;

    items.push({
      prefix: cluster.slice(lastIndex, match.index),
      citation,
    });
    lastIndex = match.index + match[0].length;
  }

  COMPLETE_CITATION_PATTERN.lastIndex = 0;

  if (items.length === 0) return '';

  // Always paint the numbered pills. Chunk-info only enriches tooltips;
  // hiding the cluster as empty `.citation-slot`s leaves a gap in the
  // answer if that lookup is slow or never returns.
  return items
    .map(({ prefix, citation }) => {
      return `${escapeHtmlText(prefix)}${renderCitationHtml(citation, chunkInfoByRequestKey)}`;
    })
    .join('');
}

function renderCitationMarker(
  marker: string,
  citationContext: CitationContext,
  chunkInfoByRequestKey: Map<string, CitationChunkInfo | null>
) {
  const parsedCitation = parseCitationMarker(marker);
  if (!parsedCitation) return '';

  const {
    cellRange,
    chunkId,
    docketEntryGuid,
    guid,
    href,
    key,
    legacyNumber,
    segmentIndex,
  } = parsedCitation;
  const citation = resolveCitation({
    citationContext,
    cellRange,
    chunkId,
    docketEntryGuid,
    guid,
    href,
    key,
    legacyNumber,
    segmentIndex,
  });
  if (!citation) return '';

  return renderCitationHtml(citation, chunkInfoByRequestKey);
}

function renderCitationHtml(
  citation: ResolvedCitation,
  chunkInfoByRequestKey: Map<string, CitationChunkInfo | null>,
) {
  if (citation.isDeleted) {
    return renderDeletedCitationHtml({
      cellRange: citation.cellRange,
      chunkId: citation.chunkId,
      citationKey: deletedCitationKey(citation),
      displayNumber: citation.displayNumber,
      documentGuid: citation.documentGuid,
      legacyNumber: citation.legacyNumber,
      segmentIndex: citation.segmentIndex,
    });
  }

  const chunkInfo = citation.requestKey
    ? chunkInfoByRequestKey.get(citation.requestKey)
    : undefined;

  const fileName = chunkInfo && (chunkInfo.display_name || chunkInfo.file_name)
    ? documentDisplayName({ displayName: chunkInfo.display_name, fileName: chunkInfo.file_name })
    : citation.fileName;
  const pageNumber = chunkInfo?.page_number ?? citation.pageNumber;
  const paragraphNumber = citation.paragraphNumber;
  const positionLabel = chunkInfo?.position_label;

  const isDocketLink = Boolean(citation.docketEntryGuid);
  const isWebLink = Boolean(citation.href) && !isDocketLink;
  const attributes: Array<[string, string]> = [
    ['class', isWebLink ? 'citation-link citation-link-web' : 'citation-link'],
    ['aria-label', isWebLink
      ? `Web citation ${citation.displayNumber}, ${citation.fileName || citation.href}`
      : `Citation ${citation.displayNumber}`],
  ];
  // Docket and web citations are real <a href> targets so keyboard and
  // Open-in-New-Tab work. Document citations stay spans (they open the side
  // panel, not a route). Streamdown's default `a` would turn the link into a
  // <button> and drop data-*; CASE_PARTNER_MARKDOWN_COMPONENTS.a keeps it.
  if ((isDocketLink || isWebLink) && citation.href) {
    attributes.push(['href', citation.href]);
    if (isWebLink) {
      attributes.push(['target', '_blank']);
      attributes.push(['rel', 'noopener noreferrer']);
    }
  } else {
    attributes.push(['role', 'button']);
    attributes.push(['tabindex', '0']);
  }
  if (citation.siteName) {
    attributes.push(['data-site-name', citation.siteName]);
  }

  if (citation.chunkId !== undefined) {
    attributes.push(['data-chunk-id', String(citation.chunkId)]);
  }
  if (citation.segmentIndex !== undefined) {
    attributes.push(['data-segment-index', String(citation.segmentIndex)]);
  }
  if (citation.cellRange) {
    attributes.push(['data-cell-range', citation.cellRange]);
  }
  if (citation.legacyNumber) {
    attributes.push(['data-cite', citation.legacyNumber]);
  }
  if (isWebLink && citation.href) {
    attributes.push(['data-web-url', citation.href]);
  }
  if (citation.documentGuid) {
    attributes.push(['data-doc-guid', citation.documentGuid]);
  }
  if (citation.docketEntryGuid) {
    attributes.push(['data-docket-entry-guid', citation.docketEntryGuid]);
  }
  if (fileName) {
    attributes.push(['data-file-name', fileName]);
  }
  if (pageNumber !== undefined) {
    attributes.push(['data-page-number', String(pageNumber)]);
  }
  if (paragraphNumber !== undefined) {
    attributes.push(['data-paragraph-number', String(paragraphNumber)]);
    attributes.push(['data-position', `Paragraph ${paragraphNumber}`]);
  }
  if (positionLabel) {
    attributes.push(['data-position', positionLabel]);
  }

  const serializedAttributes = attributes
    .map(([name, value]) => `${name}="${escapeHtmlAttribute(value)}"`)
    .join(' ');

  const tag = isDocketLink || isWebLink ? 'a' : 'span';
  return `<${tag} ${serializedAttributes}>${escapeHtmlText(citation.displayNumber)}</${tag}>`;
}

function escapeHtmlAttribute(value: string) {
  return escapeHtmlText(value).replace(/"/g, '&quot;');
}

function escapeHtmlText(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function removeCitationsSection(content: string) {
  return content
    .replace(/##\s*Citations\s*\n[\s\S]*$/i, '')
    .replace(/##\s*Citations\s*$/i, '')
    .replace(/##\s*Citat[^\n]*$/i, '');
}

function parseCitationMarker(marker: string): ParsedCitationMarker | null {
  const citation = parseCanonicalCitationMarker(marker);
  if (!citation) return null;
  if (citation.kind === 'web') {
    return { href: citation.url, key: citation.key };
  }
  if (citation.kind === 'legacy') {
    return { key: citation.key, legacyNumber: citation.legacyNumber };
  }
  if (citation.kind === 'document') {
    return { guid: citation.documentGuid, key: citation.key };
  }
  if (citation.kind === 'docket') {
    return { docketEntryGuid: citation.entryGuid, key: citation.key };
  }
  if (citation.kind === 'segment') {
    return {
      guid: citation.documentGuid,
      key: citation.key,
      segmentIndex: String(citation.index),
    };
  }
  return {
    cellRange: citation.cellRange,
    chunkId: String(citation.index),
    guid: citation.documentGuid,
    key: citation.key,
  };
}

function getCitationKey(
  guid: string,
  chunkId?: string,
  cellRange?: string,
  segmentIndex?: string,
): string {
  if (segmentIndex) return `${guid}:s${segmentIndex}`;
  if (!chunkId) return guid;
  return `${guid}:${chunkId}${cellRange ? `@${cellRange}` : ''}`;
}

function deletedCitationKey(citation: ResolvedCitation) {
  if (citation.legacyNumber) return citation.legacyNumber;
  if (!citation.documentGuid) return undefined;
  return getCitationKey(
    citation.documentGuid,
    citation.chunkId !== undefined ? String(citation.chunkId) : undefined,
    citation.cellRange,
    citation.segmentIndex !== undefined ? String(citation.segmentIndex) : undefined,
  );
}

function getCitationChunkInfoRequest(
  citation: ParsedCitationMarker,
  sources: Map<string, Source>
): CitationChunkInfoRequest | null {
  if (citation.legacyNumber) {
    const source = sources.get(citation.legacyNumber);
    if (!source || (source.chunkId === undefined && source.segmentIndex === undefined)) return null;

    return {
      documentGuid: source.documentGuid,
      ...(source.segmentIndex !== undefined
        ? { kind: 'segment', index: source.segmentIndex }
        : { kind: 'chunk', index: source.chunkId as number }),
    };
  }

  if (!citation.guid || (!citation.chunkId && !citation.segmentIndex)) return null;

  const evidenceIndex = Number.parseInt(citation.segmentIndex || citation.chunkId || '', 10);
  if (!Number.isFinite(evidenceIndex)) return null;

  return {
    documentGuid: citation.guid,
    ...(citation.segmentIndex
      ? { kind: 'segment', index: evidenceIndex }
      : { kind: 'chunk', index: evidenceIndex }),
  };
}

function getChunkInfoRequestKey(request: CitationChunkInfoRequest) {
  return request.kind === 'segment'
    ? `${request.documentGuid}:s${request.index}`
    : `${request.documentGuid}:${request.index}`;
}

function resolveCitation({
  citationContext,
  cellRange,
  chunkId,
  docketEntryGuid,
  guid,
  href,
  key: citationKey,
  legacyNumber,
  segmentIndex,
}: {
  citationContext: CitationContext;
  cellRange?: string;
  chunkId?: string;
  docketEntryGuid?: string;
  guid?: string;
  href?: string;
  key: string;
  legacyNumber?: string;
  segmentIndex?: string;
}): ResolvedCitation | null {
  if (docketEntryGuid) {
    // Docket entries are not Crimson documents, so there is nothing to look up
    // in the documents list or fetch chunk info for. AppShell reads
    // data-docket-entry-guid for in-app navigation; the <a href> is for
    // "Open in New Tab" / modifier-click.
    return {
      displayNumber:
        citationContext.citationNumbers.get(`docket:${docketEntryGuid}`) ?? '?',
      docketEntryGuid,
      href: citationContext.slug
        ? docketCitationHref(citationContext.slug, docketEntryGuid)
        : undefined,
      isDeleted: false,
    };
  }

  if (href) {
    let host = href;
    try {
      host = new URL(href).hostname.replace(/^www\./, '');
    } catch {
      host = href;
    }
    const display = lookupWebCitationDisplay(citationContext.webCitationMeta, href);
    return {
      displayNumber: citationContext.citationNumbers.get(citationKey) ?? '?',
      fileName: display?.title || host,
      href,
      isDeleted: false,
      siteName: display?.siteName,
    };
  }

  if (legacyNumber) {
    const source = citationContext.sources.get(legacyNumber);
    if (!source) return null;

    const document = citationContext.documentsByGuid?.get(source.documentGuid);
    const requestKey = source.chunkId !== undefined || source.segmentIndex !== undefined
      ? getChunkInfoRequestKey({
          documentGuid: source.documentGuid,
          ...(source.segmentIndex !== undefined
            ? { kind: 'segment', index: source.segmentIndex }
            : { kind: 'chunk', index: source.chunkId as number }),
        })
      : undefined;
    const displayNumber = citationContext.citationNumbers.get(legacyNumber) ?? legacyNumber;
    if (citationContext.documentsByGuid && !document) {
      return {
        chunkId: source.chunkId,
        displayNumber,
        documentGuid: source.documentGuid,
        isDeleted: true,
        legacyNumber,
        requestKey,
        segmentIndex: source.segmentIndex,
      };
    }

    return {
      chunkId: source.chunkId,
      displayNumber,
      documentGuid: source.documentGuid,
      fileName: document ? documentDisplayName(document) : undefined,
      isDeleted: false,
      legacyNumber,
      pageNumber: source.pageNumber,
      paragraphNumber: source.paragraphNumber,
      requestKey,
      segmentIndex: source.segmentIndex,
    };
  }

  const locator = segmentIndex ? `s${segmentIndex}${cellRange ? `@${cellRange}` : ''}` : chunkId;
  if (!guid || !isValidCitation(guid, locator)) {
    return null;
  }

  const key = getCitationKey(guid, chunkId, cellRange, segmentIndex);
  const parsedChunkId = chunkId ? Number.parseInt(chunkId, 10) : undefined;
  const parsedSegmentIndex = segmentIndex ? Number.parseInt(segmentIndex, 10) : undefined;
  const requestKey =
    parsedSegmentIndex !== undefined || parsedChunkId !== undefined
      ? getChunkInfoRequestKey({
          documentGuid: guid,
          ...(parsedSegmentIndex !== undefined
            ? { kind: 'segment', index: parsedSegmentIndex }
            : { kind: 'chunk', index: parsedChunkId as number }),
        })
      : undefined;
  const document = citationContext.documentsByGuid?.get(guid);
  if (citationContext.documentsByGuid && !document) {
    return {
      cellRange,
      chunkId: parsedChunkId,
      displayNumber: citationContext.citationNumbers.get(key) ?? '?',
      documentGuid: guid,
      isDeleted: true,
      requestKey,
      segmentIndex: parsedSegmentIndex,
    };
  }

  const source = citationContext.sources.get(key);

  return {
    cellRange,
    chunkId: parsedChunkId,
    displayNumber: citationContext.citationNumbers.get(key) ?? '?',
    documentGuid: guid,
    fileName: document ? documentDisplayName(document) : undefined,
    isDeleted: false,
    pageNumber: source?.pageNumber,
    paragraphNumber: source?.paragraphNumber,
    requestKey,
    segmentIndex: parsedSegmentIndex,
  };
}

function parsePipeTableAt(
  lines: string[],
  startLineIndex: number,
  contentEndsWithNewline: boolean
): Omit<TableMessagePart, 'id' | 'type'> & { nextLineIndex: number } | null {
  const headerLine = lines[startLineIndex];
  const separatorLine = lines[startLineIndex + 1];

  if (!isPipeRow(headerLine) || !isSeparatorRow(separatorLine)) {
    return null;
  }

  const headerCells = splitPipeRow(headerLine);
  const separatorCells = splitPipeRow(separatorLine);
  const columns = headerCells.map((label, index) => ({
    label,
    align: getColumnAlignment(separatorCells[index]),
  }));
  const rows: string[][] = [];
  let nextLineIndex = startLineIndex + 2;

  while (nextLineIndex < lines.length && isPipeRow(lines[nextLineIndex])) {
    rows.push(normalizeRow(splitPipeRow(lines[nextLineIndex]), columns.length));
    nextLineIndex += 1;
  }

  return {
    columns,
    rows,
    isPartial: !contentEndsWithNewline && nextLineIndex === lines.length,
    nextLineIndex,
  };
}

function normalizeRow(cells: string[], columnCount: number) {
  if (cells.length === columnCount) return cells;
  if (cells.length > columnCount) {
    const leadingCells = cells.slice(0, columnCount - 1);
    const trailingCells = cells.slice(columnCount - 1);
    const firstMeaningfulTrailingCell = trailingCells.findIndex((cell) => cell.trim());
    const mergedTrailingCells =
      firstMeaningfulTrailingCell === -1
        ? ['']
        : trailingCells.slice(firstMeaningfulTrailingCell);

    return [...leadingCells, mergedTrailingCells.join(' | ')];
  }
  return [...cells, ...Array.from({ length: columnCount - cells.length }, () => '')];
}

function getColumnAlignment(separatorCell?: string): TableAlignment | undefined {
  const normalized = separatorCell?.replace(/\s+/g, '') ?? '';

  if (normalized.startsWith(':') && normalized.endsWith(':')) return 'center';
  if (normalized.endsWith(':')) return 'right';
  if (normalized.startsWith(':')) return 'left';
  return undefined;
}

function getAlignmentClass(alignment?: TableAlignment) {
  if (alignment === 'center') return 'text-center';
  if (alignment === 'right') return 'text-right';
  return 'text-left';
}
